import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LanguageSelector from "@/components/LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import { MapPin, Compass, Sun, Waves, Mountain, Utensils } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

import beachImage from "@/assets/activities/beach-cote-azur.jpg";
import hikingImage from "@/assets/activities/hiking-trail.jpg";
import restaurantImage from "@/assets/activities/provencal-restaurant.jpg";
import villageImage from "@/assets/activities/provencal-village.jpg";

const ACTIVITY_CATEGORIES = [
  {
    icon: Waves,
    image: beachImage,
    title: "Plages & Activités nautiques",
    description: "Sable fin, eaux turquoise et sports nautiques à seulement 10 minutes à pied.",
    tag: "5+ activités",
  },
  {
    icon: Mountain,
    image: hikingImage,
    title: "Randonnées & Nature",
    description: "Sentiers côtiers, parcs naturels et panoramas à couper le souffle.",
    tag: "10+ itinéraires",
  },
  {
    icon: Utensils,
    image: restaurantImage,
    title: "Gastronomie locale",
    description: "Restaurants de poisson frais, marchés provençaux et vins régionaux.",
    tag: "20+ adresses",
  },
  {
    icon: Sun,
    image: villageImage,
    title: "Culture & Patrimoine",
    description: "Villages médiévaux, forts historiques et festivals locaux à l'agenda.",
    tag: "15+ sites",
  },
];

const Activities = () => {
  const { t } = useLanguage();
  const headerReveal = useReveal<HTMLDivElement>();
  const categoriesReveal = useReveal<HTMLDivElement>();
  const mapReveal = useReveal<HTMLDivElement>();

  return (
    <>
      <Helmet>
        <title>75 Activités à Faire Autour de la Villa | Douce Vague — Grau du Roi</title>
        <meta
          name="description"
          content="Découvrez 75 activités incontournables autour de la Villa Douce Vague au Grau du Roi : plages, randonnées, restaurants, sites culturels. Carte interactive incluse."
        />
        <meta
          name="keywords"
          content="activités Grau du Roi, que faire Camargue, plages Languedoc, randonnées Gard, restaurants locaux, tourisme villa"
        />
        <meta property="og:title" content="75 Activités — Villa Douce Vague, Grau du Roi" />
        <meta property="og:description" content="Carte interactive : plages, randonnées, restaurants et sites culturels autour de la villa." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TouristAttraction",
            "name": "Activités autour de Villa Douce Vague",
            "description": "75 activités et points d'intérêt autour de notre villa de vacances au Grau du Roi",
            "url": "https://www.villadoucevague.fr/activites",
            "geo": { "@type": "GeoCoordinates", "latitude": "43.52", "longitude": "4.13" },
          })}
        </script>
      </Helmet>

      <div className="min-h-screen flex flex-col bg-white">
        <Navbar rightElement={<LanguageSelector />} />

        <main className="flex-1">
          {/* Cinematic hero */}
          <section className="relative overflow-hidden bg-villa-blue" style={{ minHeight: 480 }}>
            {/* Background collage */}
            <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
              {[beachImage, hikingImage, restaurantImage, villageImage].map((src, i) => (
                <div key={i} className="relative overflow-hidden">
                  <img src={src} alt="" className="w-full h-full object-cover opacity-25" aria-hidden="true" />
                </div>
              ))}
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-villa-blue/70 via-villa-blue/80 to-villa-blue" />

            {/* Content */}
            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-40 pb-24">
              <div
                ref={headerReveal.ref}
                className={`reveal ${headerReveal.revealed ? "in" : ""}`}
              >
                <div className="flex items-center gap-2 mb-6">
                  <Compass className="h-5 w-5 text-villa-turquoise-light" strokeWidth={1.5} />
                  <p className="text-xs font-medium tracking-[0.18em] uppercase text-villa-turquoise-light">
                    Autour de la villa
                  </p>
                </div>
                <h1 className="heading-lg text-white mb-4">
                  75 activités à découvrir
                </h1>
                <p className="body-lg text-white/60 max-w-xl">
                  Plages, randonnées, gastronomie, patrimoine — explorez notre sélection des meilleures expériences autour du Grau du Roi.
                </p>
              </div>
            </div>
          </section>

          {/* Category cards */}
          <section className="py-20 md:py-28 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div
              ref={categoriesReveal.ref}
              className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 reveal ${categoriesReveal.revealed ? "in" : ""}`}
            >
              {ACTIVITY_CATEGORIES.map(({ icon: Icon, image, title, description, tag }, i) => (
                <div
                  key={i}
                  className="group relative overflow-hidden rounded-3xl bg-villa-offwhite"
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  {/* Photo */}
                  <div className="relative overflow-hidden" style={{ height: 220 }}>
                    <img
                      src={image}
                      alt={title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium border border-white/25">
                      {tag}
                    </span>
                  </div>

                  {/* Text */}
                  <div className="p-5">
                    <div className="w-9 h-9 rounded-xl bg-villa-turquoise/15 flex items-center justify-center mb-3">
                      <Icon className="h-4.5 w-4.5 text-villa-turquoise" strokeWidth={1.75} />
                    </div>
                    <h2 className="font-semibold text-villa-text text-[15px] leading-snug mb-2">{title}</h2>
                    <p className="text-villa-text-secondary text-[13px] leading-relaxed">{description}</p>
                  </div>
                </div>
              ))}

            </div>
          </section>

          {/* Interactive map */}
          <section className="bg-villa-offwhite py-20 md:py-28">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
              <div
                ref={mapReveal.ref}
                className={`reveal ${mapReveal.revealed ? "in" : ""}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="h-5 w-5 text-villa-turquoise" strokeWidth={1.75} />
                  <p className="eyebrow">Carte interactive</p>
                </div>
                <h2 className="font-playfair text-3xl font-bold text-villa-text mb-3">
                  Tous les spots à proximité
                </h2>
                <p className="text-villa-text-secondary mb-10 max-w-xl">
                  Naviguez sur la carte pour découvrir plages, sentiers, restaurants et sites culturels — tous à portée de main.
                </p>

                <div className="relative overflow-hidden rounded-3xl shadow-xl border border-black/[0.06]">
                  <div style={{ aspectRatio: "16/9" }}>
                    <iframe
                      src="https://www.google.com/maps/d/u/0/embed?mid=1503wW36C8PsMAF99AuOFtTrDXcwcGso&ehbc=2E312F"
                      width="100%"
                      height="100%"
                      style={{ border: 0, display: "block" }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Carte des 75 activités autour de la villa Douce Vague"
                    />
                  </div>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-3 text-villa-text-secondary text-sm">
                  {[
                    "Cliquez sur les marqueurs colorés pour plus d'informations",
                    "Les plages sont à 10 min à pied ou 4 min en vélo",
                    "Marchés locaux : jeudi et dimanche matin",
                  ].map((tip, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-villa-turquoise flex-shrink-0" />
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Activities;
